import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Loader2, Save, RefreshCw, Link2, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_CATEGORIES = [
  "finance", "credit_cards", "banking", "auto_finance",
  "insurance", "real_estate", "legal_tax", "business_directory",
  "health", "energy", "fashion", "clothing", "furniture", "home",
];

export default function CrossPortalConfigPage() {
  const [portals, setPortals] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [productCounts, setProductCounts] = useState({ total: 0, by_portal: {} });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, prods] = await Promise.all([
        axios.get(`${API}/admin/cross-portal/config`),
        axios.get(`${API}/admin/products/count`).catch(() => ({ data: { total: 0, by_portal: {} } })),
      ]);
      setPortals(cfg.data.portals || {});
      setProductCounts(prods.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updatePortal = (key, field, value) => {
    setPortals(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  const toggleCategory = (pkey, cat) => {
    setPortals(prev => {
      const p = { ...prev[pkey] };
      const cats = new Set(p.categories || []);
      if (cats.has(cat)) cats.delete(cat); else cats.add(cat);
      return { ...prev, [pkey]: { ...p, categories: Array.from(cats) } };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/cross-portal/config`, { portals });
      toast.success("Konfig zapisany. Nowe artykuły będą wstrzykiwać CTA wg nowego mappingu.");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Link2 className="w-8 h-8 text-amber-600" />
          <div>
            <h1 className="text-2xl font-bold">Cross-Portal Links 🔗</h1>
            <p className="text-sm text-muted-foreground">
              Mapping: kategoria treści → twój portal z produktami. Auto-wstrzykiwane do nowych artykułów.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="w-4 h-4 mr-2" /> Odśwież
          </Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Zapisz mapping
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {Object.entries(portals).map(([pkey, p]) => {
          const productCount = productCounts.by_portal[pkey] || 0;
          return (
            <Card key={pkey} className={p.enabled ? "border-emerald-200" : "border-slate-300 opacity-70"}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="text-2xl">{p.icon || "🌐"}</span>
                    {p.name}
                  </CardTitle>
                  <label className="flex items-center gap-1 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!p.enabled}
                      onChange={e => updatePortal(pkey, "enabled", e.target.checked)}
                    />
                    Włączony
                  </label>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-sm flex items-center gap-2">
                  <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                    {p.url} <ExternalLink className="w-3 h-3" />
                  </a>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-700">CTA tytuł:</label>
                  <input
                    type="text"
                    value={p.cta_title || ""}
                    onChange={e => updatePortal(pkey, "cta_title", e.target.value)}
                    className="w-full mt-1 px-2 py-1 border rounded text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">CTA tekst:</label>
                  <input
                    type="text"
                    value={p.cta_template || ""}
                    onChange={e => updatePortal(pkey, "cta_template", e.target.value)}
                    className="w-full mt-1 px-2 py-1 border rounded text-sm"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-700">Kategorie tematyczne (kliknij żeby dodać/usunąć):</label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {ALL_CATEGORIES.map(cat => {
                      const active = (p.categories || []).includes(cat);
                      return (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => toggleCategory(pkey, cat)}
                          className={`text-xs px-2 py-1 rounded-full border ${
                            active ? "bg-emerald-100 text-emerald-900 border-emerald-300"
                                  : "bg-slate-100 text-slate-600 border-slate-300 hover:bg-slate-200"
                          }`}
                        >
                          {active ? "✓ " : ""}{cat}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex items-center gap-2 text-xs text-slate-500 pt-2 border-t">
                  <Badge variant="outline">📦 {productCount} produktów z feeda</Badge>
                  {(p.categories || []).length === 0 && <Badge variant="outline" className="text-amber-700">⚠️ brak kategorii — nie zadziała</Badge>}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="p-4 text-sm text-blue-900">
          <strong>💡 Jak to działa:</strong> Każdy nowy artykuł wygenerowany przez agenta jest skanowany na kategorie (money keywords + heurystyki).
          Jeśli post pasuje do kategorii skonfigurowanej dla któregoś z twoich portali, na końcu treści (przed sekcją "Źródła") wstrzykuje się amber-CTA box z linkiem.
          Działa razem z Pillar Linker (2 internal linki) — łącznie 3 dodatkowe linki per post.
        </CardContent>
      </Card>
    </div>
  );
}
